import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  RefreshCcw,
  ExternalLink,
  Loader2,
  Trash2,
  Pencil,
  Check,
  X,
} from "lucide-react";
import { LineChart, Line, ResponsiveContainer, YAxis, Tooltip } from "recharts";
import api from "../lib/api";
import Layout from "../components/Layout";
import StatusBadge from "../components/StatusBadge";
import ChatPanel from "../components/ChatPanel";
import KnowledgeBaseEditor from "../components/KnowledgeBaseEditor";
import TaskList from "../components/TaskList";
import InsightsPanel from "../components/InsightsPanel";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const AppDetail = () => {
  const { appId } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [checks, setChecks] = useState([]);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        api.get(`/apps/${appId}`),
        api.get(`/apps/${appId}/checks`).catch(() => ({ data: [] })),
      ]);
      setApp(a.data);
      setChecks(c.data || []);
    } catch (err) {
      toast.error("Could not load project");
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [appId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const runCheck = async () => {
    if (!app?.url) {
      toast.error("Add a URL to enable health checks.");
      return;
    }
    setChecking(true);
    try {
      const { data } = await api.post(`/apps/${appId}/check`);
      setApp((prev) => ({
        ...prev,
        status: data.status,
        last_response_ms: data.response_ms,
        last_checked_at: data.checked_at,
      }));
      const fresh = await api.get(`/apps/${appId}/checks`);
      setChecks(fresh.data || []);
      toast.success(
        data.status === "healthy"
          ? `Healthy · ${data.response_ms}ms`
          : `Down (${data.status_code || "no response"})`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Check failed");
    } finally {
      setChecking(false);
    }
  };

  const removeApp = async () => {
    try {
      await api.delete(`/apps/${appId}`);
      toast.success(`${app.name} removed.`);
      navigate("/");
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const saveName = async () => {
    if (!nameDraft.trim()) {
      setEditingName(false);
      return;
    }
    try {
      const { data } = await api.patch(`/apps/${appId}`, { name: nameDraft.trim() });
      setApp(data);
      setEditingName(false);
      toast.success("Renamed.");
    } catch {
      toast.error("Rename failed");
    }
  };

  if (loading || !app) {
    return (
      <Layout>
        <div className="flex justify-center py-32">
          <Loader2 className="w-6 h-6 text-champagne animate-spin" />
        </div>
      </Layout>
    );
  }

  const sparkData = [...checks]
    .slice(0, 30)
    .reverse()
    .map((c, idx) => ({ idx, ms: c.response_ms || 0 }));

  return (
    <Layout>
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-sm text-textmuted hover:text-champagne transition-colors mb-6"
        data-testid="back-to-dashboard"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
        Back to estate
      </Link>

      <section className="regal-card p-8 mb-8 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1693648793394-0b76b7eb042e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBsdXh1cnklMjB0ZXh0dXJlfGVufDB8fHx8MTc4MTQ5MjUxMnww&ixlib=rb-4.1.0&q=85)",
            backgroundSize: "cover",
          }}
        />
        <div className="relative flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div className="flex-1 min-w-0">
            <p className="label-eyebrow">Project</p>
            <div className="flex items-center gap-3 mt-2">
              {editingName ? (
                <div className="flex items-center gap-2 flex-1">
                  <input
                    autoFocus
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveName();
                      if (e.key === "Escape") setEditingName(false);
                    }}
                    className="font-heading text-4xl tracking-tight bg-obsidian border border-seam rounded-md px-3 py-1 text-textmain focus:outline-none focus:border-champagne"
                    data-testid="app-name-input"
                  />
                  <button
                    onClick={saveName}
                    className="p-2 text-champagne hover:bg-elevated rounded-md"
                    data-testid="app-name-save"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setEditingName(false)}
                    className="p-2 text-textmuted hover:bg-elevated rounded-md"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <>
                  <h1
                    data-testid="app-name"
                    className="font-heading text-4xl sm:text-5xl tracking-tight text-textmain truncate"
                  >
                    {app.name}
                  </h1>
                  <button
                    onClick={() => {
                      setNameDraft(app.name);
                      setEditingName(true);
                    }}
                    className="p-2 rounded-md text-textmuted hover:text-champagne hover:bg-elevated transition-colors"
                    data-testid="edit-name-btn"
                    title="Rename"
                  >
                    <Pencil className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-4">
              <StatusBadge status={app.status} />
              {app.url && (
                <a
                  href={app.url.startsWith("http") ? app.url : `https://${app.url}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="app-url-link"
                  className="inline-flex items-center gap-1.5 text-sm text-textmuted hover:text-champagne transition-colors"
                >
                  {app.url}
                  <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
                </a>
              )}
            </div>

            {app.description && (
              <p className="text-textmuted leading-relaxed mt-5 max-w-3xl">
                {app.description}
              </p>
            )}

            {app.tech_stack?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-5">
                {app.tech_stack.map((t) => (
                  <span
                    key={t}
                    className="text-[10px] tracking-wider uppercase px-2 py-1 rounded-full border border-seam text-textmuted bg-elevated"
                  >
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col items-stretch lg:items-end gap-3 lg:min-w-[260px]">
            <div className="regal-card-elev p-4">
              <p className="label-eyebrow">Last response</p>
              <p className="font-sans text-3xl font-light tracking-tight mt-2">
                {app.last_response_ms != null ? `${app.last_response_ms}ms` : "—"}
              </p>
              <p className="text-xs text-textmuted mt-1">
                Checked {fmtDate(app.last_checked_at)}
              </p>
              {sparkData.length > 1 && (
                <div className="h-12 mt-3">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={sparkData}>
                      <Line
                        type="monotone"
                        dataKey="ms"
                        stroke="#D4AF37"
                        strokeWidth={1.5}
                        dot={false}
                      />
                      <YAxis hide domain={["auto", "auto"]} />
                      <Tooltip
                        contentStyle={{
                          background: "#121215",
                          border: "1px solid #27272A",
                          fontSize: 12,
                        }}
                        labelStyle={{ color: "#A1A1AA" }}
                        formatter={(v) => [`${v} ms`, "Response"]}
                        labelFormatter={() => ""}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={runCheck}
              disabled={checking}
              data-testid="run-check-btn"
              className="inline-flex items-center justify-center gap-2 bg-champagne text-obsidian hover:bg-champagne-soft font-medium rounded-md px-5 py-2.5 transition-all disabled:opacity-60"
            >
              {checking ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCcw className="w-4 h-4" strokeWidth={1.6} />
              )}
              Run health check
            </button>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  type="button"
                  data-testid="delete-app-btn"
                  className="inline-flex items-center justify-center gap-2 border border-seam text-textmuted hover:text-[#E5A09E] hover:border-[#5C2B29]/50 rounded-md px-5 py-2 text-sm transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Remove project
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-surface border-seam text-textmain">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-heading text-2xl">
                    Remove {app.name}?
                  </AlertDialogTitle>
                  <AlertDialogDescription className="text-textmuted">
                    This will permanently delete the project, its knowledge base,
                    tasks, insights, and chat history.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-transparent border-seam text-textmain hover:bg-elevated">
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={removeApp}
                    data-testid="confirm-delete-btn"
                    className="bg-[#5C2B29] text-[#E5A09E] hover:bg-[#7a3a37]"
                  >
                    Delete project
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </section>

      <Tabs defaultValue="concierge" className="w-full">
        <TabsList className="bg-surface border border-seam p-1 rounded-md">
          <TabsTrigger
            value="concierge"
            data-testid="tab-concierge"
            className="data-[state=active]:bg-elevated data-[state=active]:text-champagne text-textmuted"
          >
            Concierge
          </TabsTrigger>
          <TabsTrigger
            value="knowledge"
            data-testid="tab-knowledge"
            className="data-[state=active]:bg-elevated data-[state=active]:text-champagne text-textmuted"
          >
            Knowledge Base
          </TabsTrigger>
          <TabsTrigger
            value="tasks"
            data-testid="tab-tasks"
            className="data-[state=active]:bg-elevated data-[state=active]:text-champagne text-textmuted"
          >
            Tasks & Insights
          </TabsTrigger>
        </TabsList>

        <TabsContent value="concierge" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <ChatPanel appId={appId} appName={app.name} />
            </div>
            <div>
              <InsightsPanel appId={appId} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="knowledge" className="mt-6">
          <div className="max-w-4xl">
            <KnowledgeBaseEditor app={app} onUpdated={setApp} />
          </div>
        </TabsContent>

        <TabsContent value="tasks" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TaskList appId={appId} />
            <InsightsPanel appId={appId} />
          </div>
        </TabsContent>
      </Tabs>
    </Layout>
  );
};

export default AppDetail;
